import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, User, AlertCircle, Mail, ArrowLeft, KeyRound } from "lucide-react";
import axios from "axios";
import { API } from "@/App";

export default function AdminLogin() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("login"); // login | forgot | reset
  const [email, setEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const res = await axios.post(`${API}/login`, { username, password }, { withCredentials: true });
      if (res.data.success) {
        localStorage.setItem('admin_authenticated', 'true');
        navigate("/admin");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid username or password");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccessMsg("");

    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email });
      setSuccessMsg(res.data.message);
      setMode("reset");
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccessMsg("");

    try {
      const res = await axios.post(`${API}/auth/reset-password`, { token: resetCode, new_password: newPassword });
      setSuccessMsg("Password reset successfully! You can now login.");
      setTimeout(() => {
        setMode("login");
        setSuccessMsg("");
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid or expired reset code");
    } finally {
      setLoading(false);
    }
  };

  if (mode === "forgot" || mode === "reset") {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-4" data-testid="admin-forgot-page">
        <div className="w-full max-w-md bg-slate-900 rounded-3xl border border-slate-800 shadow-2xl overflow-hidden">
          <div className="p-8 border-b border-slate-800 bg-slate-950/50 text-center">
            <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center text-white mx-auto mb-4 shadow-lg shadow-blue-600/20">
              {mode === "forgot" ? <Mail size={32} /> : <KeyRound size={32} />}
            </div>
            <h1 className="text-2xl font-bold text-white">{mode === "forgot" ? "Forgot Password" : "Reset Password"}</h1>
            <p className="text-slate-500 text-sm mt-1">
              {mode === "forgot" ? "Enter your admin email to receive a reset code" : "Enter the code sent to your email"}
            </p>
          </div>

          <form onSubmit={mode === "forgot" ? handleForgotPassword : handleResetPassword} className="p-8 space-y-6">
            {error && (
              <div className="flex items-center gap-2 p-4 bg-red-500/10 text-red-400 rounded-xl text-sm font-medium border border-red-500/20" data-testid="reset-error">
                <AlertCircle size={18} />
                {error}
              </div>
            )}
            {successMsg && (
              <div className="flex items-center gap-2 p-4 bg-emerald-500/10 text-emerald-400 rounded-xl text-sm font-medium border border-emerald-500/20" data-testid="reset-success">
                {successMsg}
              </div>
            )}

            {mode === "forgot" ? (
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-400 flex items-center gap-2">
                  <Mail size={16} /> Admin Email
                </label>
                <input
                  required
                  type="email"
                  data-testid="reset-email-input"
                  className="w-full px-4 py-3 rounded-xl border border-slate-800 bg-slate-950 text-white placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all"
                  placeholder="Enter admin email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-400 flex items-center gap-2">
                    <KeyRound size={16} /> Reset Code
                  </label>
                  <input
                    required
                    type="text"
                    data-testid="reset-code-input"
                    className="w-full px-4 py-3 rounded-xl border border-slate-800 bg-slate-950 text-white placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all tracking-widest text-center text-lg font-mono"
                    placeholder="Enter code"
                    value={resetCode}
                    onChange={(e) => setResetCode(e.target.value.toUpperCase())}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-400 flex items-center gap-2">
                    <Lock size={16} /> New Password
                  </label>
                  <input
                    required
                    type="password"
                    data-testid="new-password-input"
                    className="w-full px-4 py-3 rounded-xl border border-slate-800 bg-slate-950 text-white placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all"
                    placeholder="Enter new password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                </div>
              </>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="reset-submit-btn"
              className="w-full py-4 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-500 transition-all shadow-lg shadow-blue-600/20 disabled:opacity-50"
            >
              {loading ? "Processing..." : mode === "forgot" ? "Send Reset Code" : "Reset Password"}
            </button>

            <button
              type="button"
              onClick={() => { setMode("login"); setError(""); setSuccessMsg(""); }}
              className="w-full flex items-center justify-center gap-2 text-slate-400 hover:text-white transition-colors text-sm"
            >
              <ArrowLeft size={16} />
              Back to Login
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4" data-testid="admin-login-page">
      <div className="w-full max-w-md bg-slate-900 rounded-3xl border border-slate-800 shadow-2xl overflow-hidden">
        <div className="p-8 border-b border-slate-800 bg-slate-950/50 text-center">
          <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center text-white mx-auto mb-4 shadow-lg shadow-blue-600/20">
            <Lock size={32} />
          </div>
          <h1 className="text-2xl font-bold text-white">Admin Login</h1>
          <p className="text-slate-500 text-sm mt-1">Please enter your credentials to continue</p>
        </div>

        <form onSubmit={handleLogin} className="p-8 space-y-6">
          {error && (
            <div className="flex items-center gap-2 p-4 bg-red-500/10 text-red-400 rounded-xl text-sm font-medium border border-red-500/20" data-testid="login-error">
              <AlertCircle size={18} />
              {error}
            </div>
          )}

          <div className="space-y-2">
            <label className="text-sm font-bold text-slate-400 flex items-center gap-2">
              <User size={16} /> Username
            </label>
            <input
              required
              type="text"
              data-testid="username-input"
              className="w-full px-4 py-3 rounded-xl border border-slate-800 bg-slate-950 text-white placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all"
              placeholder="Enter username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-bold text-slate-400 flex items-center gap-2">
              <Lock size={16} /> Password
            </label>
            <input
              required
              type="password"
              data-testid="password-input"
              className="w-full px-4 py-3 rounded-xl border border-slate-800 bg-slate-950 text-white placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all"
              placeholder="Enter password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit-btn"
            className="w-full py-4 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-500 transition-all shadow-lg shadow-blue-600/20 disabled:opacity-50"
          >
            {loading ? "Logging in..." : "Login to Dashboard"}
          </button>

          <button
            type="button"
            onClick={() => { setMode("forgot"); setError(""); }}
            data-testid="forgot-password-link"
            className="w-full text-center text-slate-500 hover:text-blue-400 transition-colors text-sm"
          >
            Forgot Password?
          </button>
        </form>
      </div>
    </div>
  );
}
